import { PrinterIcon } from 'lucide-react'

import { useCurrentShow } from '@/context/current-show'
import { SelectShowNotice } from '@/components/current-show'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useMemberAwardCards } from '@/api/hooks'
import { triggerReportPrint } from '@/lib/print'

import '@/styles/award-cards.css'

export default function MemberAwardCardsPage() {
  const { currentShow } = useCurrentShow()
  const showId = currentShow?.id
  const cardsQuery = useMemberAwardCards(showId)

  if (!currentShow) {
    return <SelectShowNotice description="Choose a show to generate AHSTC member award cards." />
  }

  const showName = currentShow.name ?? 'Selected Show'
  const cardsData = cardsQuery.data
  const cards = cardsData?.cards ?? []
  const recipients = cardsData?.recipients ?? []
  const summary = cardsData?.summary

  const totalPoints = recipients.reduce((sum, recipient) => sum + recipient.totalPoints, 0)

  const header = (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="space-y-1">
        <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {showName}
        </div>
        <h1 className="text-2xl font-semibold">Member Award Cards</h1>
        <p className="text-sm text-muted-foreground">
          One card per class placement for AHSTC members. Cards display official Exhibit IDs and
          placement points.
        </p>
        {summary ? (
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Entrants</dt>
              <dd className="font-semibold">{summary.entrants}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Entries</dt>
              <dd className="font-semibold">{summary.entries}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Member Placements</dt>
              <dd className="font-semibold">{cards.length}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Member Points</dt>
              <dd className="font-semibold">{totalPoints}</dd>
            </div>
          </dl>
        ) : null}
      </div>
      <Button variant="outline" onClick={triggerReportPrint} className="self-start">
        <PrinterIcon className="mr-2 h-4 w-4" /> Print cards
      </Button>
    </div>
  )

  if (cardsQuery.isLoading) {
    return (
      <ReportLayout header={header}>
        <p className="text-sm text-muted-foreground">Loading award cards…</p>
      </ReportLayout>
    )
  }

  if (cardsQuery.isError) {
    return (
      <ReportLayout header={header}>
        <p className="text-sm text-destructive">
          {cardsQuery.error?.message ?? 'Failed to load member award cards.'}
        </p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header} className="award-card-report">
      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No AHSTC member placements have been recorded for this show yet.
        </p>
      ) : (
        <div className="award-card-grid">
          {cards.map(card => {
            const classLabel = card.classCode
              ? `${card.classCode} • ${card.className}`
              : card.className
            const membershipExpiresAt = card.membershipExpiresAt
              ? new Date(card.membershipExpiresAt)
              : null
            return (
              <article
                key={`${card.exhibitorId}-${card.exhibitId}-${card.placement}`}
                className="award-card"
              >
                <header className="award-card__header">
                  <Badge variant="secondary" className="award-card__placement">
                    {card.placement}
                  </Badge>
                  <span className="award-card__points">{card.points} pts</span>
                </header>
                <div className="award-card__member">
                  <h2 className="award-card__member-name">{card.memberName}</h2>
                  <p className="award-card__exhibitor">Exhibitor: {card.exhibitorName}</p>
                  {card.memberId ? (
                    <p className="award-card__member-id">AHSTC Member ID: {card.memberId}</p>
                  ) : null}
                  {membershipExpiresAt ? (
                    <p className="award-card__membership-expiry">
                      Membership valid through{' '}
                      {membershipExpiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' })}
                    </p>
                  ) : null}
                  {card.staffRole ? (
                    <p className="award-card__staff-role">Staff Role: {card.staffRole}</p>
                  ) : null}
                </div>
                <div className="award-card__body">
                  <div className="award-card__exhibit-id">
                    Exhibit <span>{card.exhibitId}</span>
                  </div>
                  <div className="award-card__class">
                    <p className="award-card__class-name">{classLabel}</p>
                    {card.divisionName ? (
                      <p className="award-card__division">Division: {card.divisionName}</p>
                    ) : null}
                    {card.entryTitle ? (
                      <p className="award-card__entry-title">“{card.entryTitle}”</p>
                    ) : null}
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {recipients.length > 0 ? (
        <footer className="award-card__footer">
          <p className="text-xs text-muted-foreground">
            {recipients.length} AHSTC member{recipients.length === 1 ? '' : 's'} will receive email
            summaries that include Exhibit IDs and placement points.
          </p>
        </footer>
      ) : null}
    </ReportLayout>
  )
}
